<script setup lang="ts">
import type { IMediaTableFilters } from "./mediaTableFilters.interface"
import type { IListMediaQuery } from "@/api/listMedia"
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { Media } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { templateRef } from "@vueuse/core"
import { computed, ref, watch } from "vue"
import { LIST_MEDIA_QUERY_KEY, LIST_TAGS_KEY, useGetEventVault, useListMedia, useListTags, usePinMedia } from "@/api"
import { MediaDeleteModal, MediaModerateModal, MediaMoveModal, MediaTagsModal, MediaViewModal } from "@/components"
import { Badge, Banner, Button, ButtonGroup, Icon, NoData, Pagination, Popover, Slider, Table } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useFilters, useModal, usePagination, useTranslation } from "@/composables"
import { MediaDeleteModalKey, MediaFiltersDrawerKey, MediaModerateModalKey, MediaMoveModalKey, MediaTagsModalKey, MediaViewModalKey } from "@/symbols"
import { TaggingBehavior } from "@/types"
import { ViewMode } from "@/utils"
import MediaImageTile from "./MediaImageTile.vue"
import MediaTableFilters from "./MediaTableFilters.vue"
import { useManageMediaView } from "./useManageMediaView"

interface MediaTableProps {
  eventVaultId?: number
  albumId?: number
}

const props = defineProps<MediaTableProps>()

const toast = useToast()

const { t } = useTranslation()

const selectedMedia = ref<Media[]>([])
const singleMediaSelection = ref<Media>()
const showFavouriteConfirm = ref(false)

function closePopovers() {
  showFavouriteConfirm.value = false
}

defineExpose({ selectedMedia, closePopovers })

const tableWrapperRef = templateRef<HTMLDivElement>("tableWrapperRef")
const pendingApprovalCount = ref(0)
const totalMedia = ref(0)
const pageCount = ref(1)

const { filters, update: updateFilters, count: filterCount } = useFilters<IMediaTableFilters>({
  perPage: 25,
  albumId: undefined,
  awaitingModeration: false,
  sortBy: undefined,
  orderBy: undefined,
  type: [],
  tags: [],
  showPinnedUploadsOnly: false,
})

watch(
  () => props.albumId,
  id => updateFilters({ ...filters, albumId: id }),
  { immediate: true },
)

const albumIdRef = computed(() => filters.albumId)

const pagination = usePagination({
  pageCount,
  initialPerPage: computed(() => filters.perPage),
  scrollStartRef: tableWrapperRef,
})

const { toggle: toggleFilters } = useModal(MediaFiltersDrawerKey)
const { toggle: toggleMove } = useModal(MediaMoveModalKey)
const { toggle: toggleDelete } = useModal(MediaDeleteModalKey)
const { toggle: toggleTags } = useModal(MediaTagsModalKey)
const { toggle: toggleMediaView } = useModal(MediaViewModalKey)
const { toggle: toggleMediaModerate } = useModal(MediaModerateModalKey)

const listMediaQueryParams = computed<IListMediaQuery>(() => ({
  page: pagination.page.value,
  perPage: filters.perPage,
  include: ["album", "tags"],
  awaitingModeration: filters.awaitingModeration,
  sortBy: filters.sortBy,
  orderBy: filters.orderBy,
  type: filters.type?.length ? filters.type.join(",") : undefined,
  tags: filters.tags?.length ? filters.tags : undefined,
  showPinnedUploadsOnly: filters.showPinnedUploadsOnly,
}))

const { mode, setViewMode, gridColsClass, tileSizeLabel, tileSizeSliderValue } = useManageMediaView()

const { data: albumMedia, isLoading: isMediaLoading, isFetching: isMediaFetching } = useListMedia(props.eventVaultId, albumIdRef, listMediaQueryParams, {
  pageCountRef: pageCount,
  totalMediaRef: totalMedia,
  pendingApprovalCountRef: pendingApprovalCount,
})

const { data: eventVault, isLoading: isEventVaultLoading } = useGetEventVault(props.eventVaultId)

const isTaggingEnabled = computed(() => eventVault.value?.eventVaultSettings?.taggingBehavior === TaggingBehavior.On)

const { data: allEventTags, isLoading: isEventTagsLoading } = useListTags(props.eventVaultId, { enabled: isTaggingEnabled })

const { mutate: mutatePinMedia, isPending: isPinningMultipleMedia } = usePinMedia()

const isLoading = computed(() => isMediaLoading.value || isMediaFetching.value || isEventVaultLoading.value || isEventTagsLoading.value || isPinningMultipleMedia.value)

const media = computed<Media[]>(() => albumMedia.value?.media ?? [])

const isAllMediaSelected = computed(() => media.value.length > 0 && selectedMedia.value.length === media.value.length)

const isSomeMediaSelected = computed(() => selectedMedia.value.length > 0 && selectedMedia.value.length < media.value.length)

const canReviewModeration = computed(() => selectedMedia.value.every(media => media.requiresModeration) && selectedMedia.value.length)

const isModerationFeatureEnabled = computed(() => eventVault.value?.eventVaultSettings?.moderatePhotosAndVideos === true || media.value.some(media => media.requiresModeration))

const headers = computed<ITableHeader<Media>[]>(() => {
  const headers: ITableHeader<Media>[] = [
    { label: t("media_table.headers.title"), value: "title" },
    { label: t("media_table.headers.uploader"), value: "uploadedBy" },
    { label: t("media_table.headers.type"), value: "type" },
  ]

  if (isModerationFeatureEnabled.value) {
    headers.push({ label: t("media_table.moderation"), value: "requiresModeration" })
  }

  headers.push({ label: t("media_table.headers.actions"), value: "actions" })

  return headers
})

const queryClient = useQueryClient()

function clearSelectedMedia() {
  selectedMedia.value = []
  singleMediaSelection.value = undefined
  showFavouriteConfirm.value = false
}

function onMediaActionSuccess(message: string) {
  clearSelectedMedia()
  queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_QUERY_KEY] })
  toast.success({ description: message })
}

function onTagsSuccess() {
  queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_QUERY_KEY] })
  queryClient.invalidateQueries({ queryKey: [LIST_TAGS_KEY] })
}

function onSingleMediaActionOpen(toggleAction: () => void, singleItem: Media, clearOtherSelectedMedia: boolean = true) {
  singleMediaSelection.value = singleItem
  clearOtherSelectedMedia && (selectedMedia.value = [])
  toggleAction()
}

const moderateModalType = ref<"approve" | "reject">("reject")

function onModerateModalOpen(media: Media[], type: "approve" | "reject") {
  moderateModalType.value = type
  if (media.length === 1) {
    singleMediaSelection.value = media[0]
    selectedMedia.value = []
  }
  else {
    singleMediaSelection.value = undefined
  }
  toggleMediaModerate()
}

function onPinMedia(media: Media[], isPinned: boolean) {
  if (!props.eventVaultId) {
    toast.error()
    return
  }

  showFavouriteConfirm.value = false

  mutatePinMedia({ eventVaultId: props.eventVaultId, isPinned, media }, {
    onSuccess: () => onMediaActionSuccess(t("media_table.favourite_success")),
    onError: () => toast.error(),
  })
}

watch(
  () => [filters.albumId, filters.perPage, pagination.page.value],
  () => {
    clearSelectedMedia()
  },
)
</script>

<template>
  <div ref="tableWrapperRef" class="flex flex-col gap-4">
    <div class="flex flex-col gap-4">
      <h1 class="font-comfortaa font-bold text-xl">
        {{ t("media_table.title") }}
      </h1>
      <div class="text-label text-sm md:text-base font-montserrat flex flex-col max-w-3xl">
        <p data-test="media-count">
          {{ t("media_table.count", { count: totalMedia }) }}
        </p>
        <p />
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <div class="flex justify-end gap-2">
        <ButtonGroup>
          <Button
            data-test="table_view_button" variant="tertiary"
            :disabled="mode === ViewMode.TABLE" @click="setViewMode(ViewMode.TABLE)"
          >
            <Icon icon="Table" />
            <span class="hidden md:block">{{ t("host_dashboard.table_view") }}</span>
            <span class="sr-only">{{ t("host_dashboard.select_table_view") }}</span>
          </Button>
          <Button
            data-test="card_view_button" variant="tertiary"
            :disabled="mode === ViewMode.CARD" @click="setViewMode(ViewMode.CARD)"
          >
            <Icon icon="Grid" />
            <span class="hidden md:block">{{ t("host_dashboard.card_view") }}</span>
            <span class="sr-only">{{ t("host_dashboard.select_card_view") }}</span>
          </Button>
        </ButtonGroup>
        <Badge :count="filterCount">
          <Button data-test="filter-button" variant="icon" @click="toggleFilters">
            <span class="sr-only md:not-sr-only">{{ t("filter") }}</span>
            <Icon icon="Filter" />
          </Button>
        </Badge>
      </div>

      <Banner
        v-if="albumMedia?.meta?.totalAwaitingApproval && !filters.awaitingModeration"
        variant="warning"
        prefix-icon="EyeOn"
        :title="t('media_table.pending_approval_banner_title')"
        :description="t('media_table.pending_approval_banner_description', { count: pendingApprovalCount })"
      >
        <template #actions>
          <div class="flex">
            <Button variant="tertiary" class="flex-1" @click="updateFilters({ ...filters, awaitingModeration: true })">
              {{ t('media_table.pending_approval_banner_action') }}
            </Button>
          </div>
        </template>
      </Banner>

      <!-- bulk actions -->
      <div data-test="bulk-actions" class="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 p-3">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          <span class="text-xs font-semibold uppercase tracking-wide text-gray-600">{{ t("media_table.bulk_actions") }}</span>
          <div class="flex items-center gap-2 flex-wrap">
            <Button variant="danger" :disabled="!selectedMedia.length" @click="toggleDelete">
              <span class="sr-only md:not-sr-only">{{ t("media_table.delete") }}</span>
              <span data-test="delete-selected-count">({{ selectedMedia.length }})</span>
              <Icon icon="Trash" />
            </Button>
            <Button variant="primary" :disabled="!selectedMedia.length" @click="toggleMove">
              <span class="sr-only md:not-sr-only">{{ t("media_table.move") }}</span>
              <span data-test="move-selected-count">({{ selectedMedia.length }})</span>
              <Icon icon="MoveToFolder" />
            </Button>
            <Popover
              v-model:open="showFavouriteConfirm"
              side="bottom"
              :side-offset="6"
            >
              <Button variant="primary" :disabled="!selectedMedia.length">
                <span class="sr-only md:not-sr-only">{{ t("media_table.favourite") }}</span>
                <span data-test="favourite-selected-count">({{ selectedMedia.length }})</span>
                <Icon icon="StarSolid" />
              </Button>
              <template #content="{ close }">
                <p class="mb-3 font-medium">
                  {{ t("media_table.favourite_selected_count", { count: selectedMedia.length }) }}
                </p>
                <div class="flex justify-end gap-2">
                  <Button variant="tertiary" prefix-icon="Star" size="sm" data-test="favourite-confirm-cancel" @click="() => { onPinMedia(selectedMedia, true); close() }">
                    {{ t("media_table.favourite") }}
                  </Button>
                  <Button variant="tertiary" prefix-icon="StarOff" size="sm" data-test="favourite-confirm-submit" @click="() => { onPinMedia(selectedMedia, false); close() }">
                    {{ t("media_table.unfavourite") }}
                  </Button>
                </div>
              </template>
            </Popover>
            <Button
              v-if="isTaggingEnabled"
              variant="primary"
              :disabled="!selectedMedia.length"
              @click="toggleTags"
            >
              <span class="sr-only md:not-sr-only">{{ t("media_table.add_tags") }}</span>
              <span data-test="tags-selected-count">({{ selectedMedia.length }})</span>
              <Icon icon="Tag" />
            </Button>
          </div>
        </div>
        <div v-if="canReviewModeration" class="flex flex-col sm:flex-row  sm:items-center gap-3">
          <span class="text-xs font-semibold uppercase tracking-wide text-gray-600">{{ t("media_table.moderation") }}</span>
          <ButtonGroup class="rounded-lg overflow-hidden">
            <Button
              variant="success"
              :disabled="!selectedMedia.length"
              data-test="approve-selected"
              @click="onModerateModalOpen(selectedMedia, 'approve')"
            >
              <span class="sr-only md:not-sr-only">{{ t("media_table.approve") }}</span>
              <span class="md:not-sr-only">({{ selectedMedia.length }})</span>
              <Icon icon="CircleCheck" />
            </Button>
            <Button
              variant="danger"
              :disabled="!selectedMedia.length"
              data-test="reject-selected"
              @click="onModerateModalOpen(selectedMedia, 'reject')"
            >
              <span class="sr-only md:not-sr-only">{{ t("media_table.reject") }}</span>
              <span class="md:not-sr-only">({{ selectedMedia.length }})</span>
              <Icon icon="CircleRemove" />
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </div>

    <Table
      v-if="mode === ViewMode.TABLE"
      v-model="selectedMedia"
      :headers="headers"
      :data="media"
      :is-loading="isLoading"
      show-select
      show-pagination
      :pagination="pagination"
      @pagination-previous="pagination.previous"
      @pagination-next="pagination.next"
    >
      <template #empty>
        <tbody>
          <tr>
            <td :colspan="headers.length + 1">
              <NoData :headline="t('media_table.no_media')" :content="t('media_table.no_media_message')" hide-button />
            </td>
          </tr>
        </tbody>
      </template>
      <template #data-title="{ data }">
        <div class="flex flex-row gap-3 md:gap-5 overflow-hidden items-start">
          <button type="button" class="cursor-pointer" @dblclick="() => onSingleMediaActionOpen(toggleMediaView, data, false)">
            <img
              :src="data.getThumbnailUrl()"
              :alt="data.title || data.fileName"
              class="object-cover bg-secondary text-transparent w-16 md:w-28 aspect-square overflow-hidden rounded-2xl shrink-0"
            >
          </button>
          <div class="flex flex-col gap-1.5 max-w-48 overflow-hidden">
            <span class="text-ellipsis">{{ data?.title ? data?.title : "—" }}</span>
            <span v-if="data?.fileName" class="overflow-hidden text-ellipsis">{{ data?.fileName }}</span>
            <div class="flex gap-2 items-center ">
              <Icon icon="Folder" class="hidden md:inline text-secondary shrink-0" />
              <span class="font-comfortaa font-bold text-xs text-gray-500 overflow-hidden text-ellipsis">
                {{ data?.album?.title ?? "Main Gallery" }}
              </span>
            </div>
          </div>
        </div>
      </template>
      <template #data-type="{ data }">
        <span>{{ data?.type === "vid" ? t("video") : t("image") }}</span>
      </template>
      <template #data-requiresModeration="{ data }">
        <div v-if="data?.requiresModeration" class="flex flex-col gap-1.5">
          <div class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-[#FFF4CE] text-[#664400] text-xs font-medium w-fit">
            <Icon icon="EyeOn" size="sm" />
            <span>{{ t("media_table.awaiting_approval") }}</span>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <Button variant="success" size="xs" @click="onModerateModalOpen([data], 'approve')">
              <span>{{ t("media_table.approve") }}</span>
            </Button>
            <Button variant="danger" size="xs" @click="onModerateModalOpen([data], 'reject')">
              <span>{{ t("media_table.reject") }}</span>
            </Button>
          </div>
        </div>
        <span v-else class="text-gray-400">—</span>
      </template>
      <template #data-actions="{ data }">
        <div class="flex">
          <Button variant="flat" @click="() => onSingleMediaActionOpen(toggleMove, data)">
            <span class="sr-only">{{ t("media_table.move_sr") }}</span>
            <Icon icon="MoveToFolder" />
          </Button>
          <Button variant="flat" @click="() => onPinMedia([data], !Boolean(data.isPinned))">
            <span class="sr-only">{{ t("media_table.favourite_sr") }}</span>
            <Icon :icon="data.isPinned ? 'StarSolid' : 'Star'" />
          </Button>
          <Button
            v-if="eventVault?.eventVaultSettings?.taggingBehavior === TaggingBehavior.On"
            variant="flat" @click="() => onSingleMediaActionOpen(toggleTags, data)"
          >
            <span class="sr-only">{{ t("media_table.edit_sr") }}</span>
            <Icon icon="Tag" />
          </Button>
          <Button variant="flat" @click="() => onSingleMediaActionOpen(toggleDelete, data)">
            <span class="sr-only">{{ t("media_table.delete_sr") }}</span>
            <Icon icon="Trash" />
          </Button>
        </div>
      </template>
    </Table>

    <div v-if="mode === ViewMode.CARD">
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4 mb-4 p-3 md:px-4 md:py-3 rounded-xl border border-secondary bg-footer-bg">
        <div class="flex flex-wrap items-center gap-2 md:gap-3 min-w-0">
          <Button v-if="!isAllMediaSelected" variant="flat" prefix-icon="CheckCheck" class="p-0!" @click="() => selectedMedia = [...media]">
            <span class="hidden md:inline">{{ t("rsvp-list-page.select_all") }}</span>
            <span class="md:hidden">{{ t("rsvp-list-page.select_all") }}</span>
          </Button>
          <Button v-if="isAllMediaSelected || isSomeMediaSelected" variant="flat" prefix-icon="Close" class="p-0!" @click="() => selectedMedia = []">
            <span class="hidden md:inline">{{ t("media_table.clear_selection") }}</span>
            <span class="md:hidden">{{ t("clear") }}</span>
          </Button>
          <span v-if="selectedMedia.length > 0" class="text-sm text-gray-600 font-medium whitespace-nowrap">
            {{ selectedMedia.length }} {{ selectedMedia.length === 1 ? "item" : "items" }} selected
          </span>
        </div>

        <div class="w-full min-w-0 md:w-62.5 md:shrink-0">
          <Slider v-model="tileSizeSliderValue" :min="0" :max="3" :step="1" aria-label="Tile size" data-test="tile-size-slider">
            <template #icon>
              <Icon icon="Grid" class="text-gray-600 shrink-0" />
            </template>
            <template #right>
              <span class="text-sm text-gray-600 font-medium w-14 text-right shrink-0">
                {{ tileSizeLabel }}
              </span>
            </template>
          </Slider>
        </div>
      </div>

      <div v-if="isLoading" class="grid gap-4 mb-5" :class="[gridColsClass]">
        <div v-for="n in pagination.perPage.value" :key="`skeleton-${n}`" class="relative bg-white rounded-lg overflow-hidden border border-gray-200">
          <div class="relative aspect-square overflow-hidden bg-gray-100">
            <div class="gpx-skeleton w-full h-full rounded-none" />
          </div>
          <div class="p-3 bg-white">
            <div class="gpx-skeleton h-4 w-3/4 mb-2" />
            <div class="gpx-skeleton h-3 w-1/2" />
          </div>
        </div>
      </div>

      <div v-else-if="media.length > 0" class="grid gap-4 mb-5" :class="[gridColsClass]">
        <MediaImageTile
          v-for="(mediaItem, index) in media"
          :key="`media-${index}`"
          :media="mediaItem"
          :is-tagging-enabled="isTaggingEnabled"
          :is-selected="selectedMedia.some(media => media.id === mediaItem.id)"
          @select="(media) => selectedMedia.push(media)"
          @deselect="(media) => selectedMedia = selectedMedia.filter(item => item.id !== media.id)"
          @move="(media) => onSingleMediaActionOpen(toggleMove, media)"
          @tag="(media) => onSingleMediaActionOpen(toggleTags, media)"
          @delete="(media) => onSingleMediaActionOpen(toggleDelete, media)"
          @view="(media) => onSingleMediaActionOpen(toggleMediaView, media, false)"
          @approve="(media) => onModerateModalOpen([media], 'approve')"
          @reject="(media) => onModerateModalOpen([media], 'reject')"
          @favourite="(media) => onPinMedia([media], !Boolean(media.isPinned))"
        />
      </div>

      <div v-else-if="!isLoading" class="mb-5">
        <NoData
          :headline="t('media_table.no_media')"
          :content="t('media_table.no_media_message')"
          hide-button
        />
      </div>

      <Pagination
        :current-page="pagination.page.value"
        :page-count="pagination.pageCount?.value"
        :per-page="pagination.perPage.value"
        :can-next="pagination.canNext.value"
        :can-previous="pagination.canPrevious.value"
        @next="pagination.next"
        @previous="pagination.previous"
      />
    </div>
  </div>

  <MediaTableFilters
    :filters="filters"
    :event-vault-id="eventVaultId"
    :tagging-enabled="isTaggingEnabled"
    :event-tags="allEventTags ?? []"
    :show-moderation-filter="isModerationFeatureEnabled"
    @update:filters="(updatedFilters: Partial<IMediaTableFilters>) => {
      updateFilters({ ...filters, ...updatedFilters })
      pagination.page.value = 1
      clearSelectedMedia()
    }"
  />
  <MediaMoveModal
    :media="singleMediaSelection ? [singleMediaSelection] : selectedMedia"
    @close="clearSelectedMedia"
    @success="onMediaActionSuccess(t('media_table.move_successful'))"
    @error="() => toast.error()"
  />
  <MediaDeleteModal
    :media="singleMediaSelection ? [singleMediaSelection] : selectedMedia"
    @success="onMediaActionSuccess(t('media_table.delete_successful'))"
    @error="() => toast.error()"
  />
  <MediaTagsModal
    :media="singleMediaSelection ? [singleMediaSelection] : selectedMedia"
    :event-vault-id="eventVaultId"
    :event-tags="allEventTags ?? []"
    :is-loading="isLoading"
    @success="onTagsSuccess"
    @error="() => toast.error()"
    @close="clearSelectedMedia"
  />
  <MediaModerateModal
    :media="singleMediaSelection ? [singleMediaSelection] : selectedMedia"
    :type="moderateModalType"
    @success="onMediaActionSuccess(t('media_table.moderation_successful'))"
    @close="clearSelectedMedia"
    @error="() => toast.error()"
  />
  <MediaViewModal
    :media="singleMediaSelection"
    :is-tagging-enabled="isTaggingEnabled"
    @close="singleMediaSelection = undefined"
  />
</template>
